// Summary 탭 — 바닐라 renderResult 의 KPI/주요가정/검토요약 부분 이식.
// result JSON 소비 필드: bridge.{operating_value,equity_value,price_per_share,
// diluted_price_per_share,diluted_shares_outstanding}, pv.pv_terminal,
// terminal.g, wacc.value, meta.period_labels, forecast.revenue
import { fmt, pct } from "../../lib/format";
import { unitMoney, unitShare, useT, yearCol } from "../../lib/i18n";
import { useDcf } from "../../stores/dcf";

export default function SummaryTab() {
  const { lang, t } = useT();
  const current = useDcf((s) => s.current);
  if (!current) return null;
  const p = current.payload;
  const r = current.result;
  const b = r?.bridge || {};
  const term = r?.terminal || {};
  const pv = r?.pv || {};

  const ev = b.operating_value;
  const eq = b.equity_value;
  const pps = b.diluted_price_per_share || b.price_per_share;
  const tvRatio = pv.pv_terminal && ev ? pv.pv_terminal / ev : null;
  const wacc = r?.wacc?.value ?? p.discount_rate;
  const g = term.g ?? p.terminal_growth_rate;
  const labels =
    r?.meta?.period_labels || [1, 2, 3, 4, 5].map((n) => yearCol(lang, n));

  const dilPps = b.diluted_price_per_share;
  const basicPps = b.price_per_share;
  const showDil =
    dilPps != null && basicPps != null && Math.abs(dilPps - basicPps) > 1;

  // 바닐라 renderResult 의 summary-comment 자동 생성 문구
  const fcRev = r?.forecast?.revenue;
  const cagr =
    fcRev && fcRev.length > 1
      ? Math.pow(fcRev[fcRev.length - 1] / fcRev[0], 1 / (fcRev.length - 1)) - 1
      : null;
  const comment = r
    ? `① Terminal Value가 영업가치의 ${pct(tvRatio)}를 차지해 WACC·영구성장률 가정 민감도가 ${
        (tvRatio ?? 0) > 0.7 ? "높은 편입니다" : "적정한 수준입니다"
      }.\n\n② 추정기간 매출 CAGR ${pct(cagr)}${
        (cagr ?? 0) > 0.2
          ? ", 성장 근거에 대한 추가 검토가 필요합니다"
          : " 합리적 범위로 판단됩니다"
      }.\n\n③ WACC ${pct(wacc)} − 영구성장률 ${pct(g)} spread = ${pct(
        wacc != null && g != null ? wacc - g : null,
      )}.`
    : t("ph.recalc_auto");

  return (
    <>
      <div className="kpi-grid">
        <div className="kpi-card">
          <div className="kpi-label">Operating Value</div>
          <div className="kpi-val green">{fmt(ev)}</div>
          <div className="kpi-sub">{unitMoney(lang)}</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Equity Value</div>
          <div className="kpi-val">{fmt(eq)}</div>
          <div className="kpi-sub">{unitMoney(lang)}</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Price per Share</div>
          <div className="kpi-val">{fmt(pps)}</div>
          <div className="kpi-sub">{unitShare(lang)}</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">TV / EV Ratio</div>
          <div className="kpi-val">{pct(tvRatio)}</div>
          <div className="kpi-sub">{t("kpi.tv_sub")}</div>
        </div>
      </div>
      <div className="data-card">
        <h4>{t("h.key_assumptions")}</h4>
        <table className="data-table">
          <tbody>
            <tr>
              <td>{t("lbl.company")}</td>
              <td>{p.target_name || "—"}</td>
            </tr>
            <tr>
              <td>{t("lbl.val_date")}</td>
              <td>{p.valuation_date || "—"}</td>
            </tr>
            <tr>
              <td>{t("lbl.forecast_period")}</td>
              <td>
                {r
                  ? `${p.forecast_full_years || 5}${t("unit.years_suffix")} (${labels[0]}~${labels[labels.length - 1]})`
                  : "—"}
              </td>
            </tr>
            <tr>
              <td>{t("lbl.wacc_row")}</td>
              <td>{pct(wacc)}</td>
            </tr>
            <tr>
              <td>{t("lbl.tgr_row")}</td>
              <td>{pct(g)}</td>
            </tr>
            <tr>
              <td>{t("lbl.tax_row")}</td>
              <td>{pct(p.tax_rate)}</td>
            </tr>
            <tr>
              <td>{t("lbl.net_debt")}</td>
              <td>
                {fmt(p.non_operating?.interest_bearing_debt)}
                {unitMoney(lang)}
              </td>
            </tr>
            <tr>
              <td>{t("lbl.shares_row")}</td>
              <td>
                {fmt(p.non_operating?.shares_outstanding)}
                {t("unit.shares_suffix")}
              </td>
            </tr>
            {showDil && (
              <tr>
                <td>{t("lbl.diluted_row")}</td>
                <td>
                  {fmt(b.diluted_shares_outstanding)}
                  {t("unit.shares_suffix")}
                </td>
              </tr>
            )}
            {showDil && (
              <tr>
                <td>{t("lbl.diluted_pps_row")}</td>
                <td>
                  {fmt(dilPps)}
                  {unitShare(lang)}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="agent-comment">
        <div className="comment-header">{t("h.agent_summary")}</div>
        <p>{comment}</p>
      </div>
    </>
  );
}
